import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useResourceDataContextSafe } from '../../../../resource-detail/components/context/resourceDataContext.js';
import { IconSharedWithPublic } from '../../IconSharedWithPublic.js';
import { ResourcePreviewContent } from './Content/ResourcePreviewContent.js';
import { containerPreviewCompact } from './cssPreviewContainer.js';
import { ResourcePreviewPinnedComment } from './ResourcePreviewComment.js';
import { ResourcePreviewName } from './ResourcePreviewName.js';
import { ResourcePreviewTags } from './ResourcePreviewTags.js';
import { useResourcePreviewUiConfigContext } from '../resourcePreviewUiConfigContext.js';
import { isResourceDetailFolder, isResourceDetailNotepad, isResourceDetailImage, isResourceDetailVideo } from '../../../utils/resourceDetailGuards.js';
import React__default from 'react';
import { styled, css } from 'styled-components';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { LinkExternal } from '@fabric-app/ui/components/Link/LinkExternal';
import { PreviewGridContainer, PreviewGridSizingContainer, PreviewGridInnerLayout, ResourcePreviewGridItemMetaContent } from './components/PreviewContainer.js';
import { SPOTIFY_URL_REGEX } from '@fabric-app/utils/url';
import { getOriginUrlWithHash } from '../../../utils/getOriginUrlWithHash.js';
import { useSetQueryResourceDetailData } from '../../../queries/useQueryResourceDetail.js';
import { ResourcePreviewInnerFolder } from './Content/ResourcePreviewInnerFolder.js';
import { FloatingExternalLinkButton } from './components/FloatingExternalLinkButton.js';

const getResourcePreviewVariant = (resource) => {
  if (resource.originUrl && resource.originUrl.match(SPOTIFY_URL_REGEX)) {
    return "spotify";
  }
  if (isResourceDetailNotepad(resource)) {
    return "notepad";
  }
  if (resource.extension && [
    "md",
    "txt",
    "docx",
    "doc",
    "html",
    "csv"
  ].includes(resource.extension)) {
    return "text-document";
  }
  if ((isResourceDetailImage(resource) || isResourceDetailVideo(resource)) && !resource.originUrl && resource.thumbnail?.md) {
    return "full-thumbnail-image";
  }
  return void 0;
};
const COMMENT_SECTION_HEIGHT = 33;
const Inner = ({ children, appearence }) => {
  const { resource } = useResourceDataContextSafe();
  const previewVariant = getResourcePreviewVariant(resource);
  const comment = resource.commentPinned;
  const tags = resource.tags;
  if (isResourceDetailFolder(resource)) {
    return /* @__PURE__ */ jsx(ResourcePreviewInnerFolder, {});
  }
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      children,
      /* @__PURE__ */ jsx(ResourcePreviewGridItemContentPreview, {
        children: /* @__PURE__ */ jsx(ResourcePreviewContent, {})
      }),
      !appearence?.hideMeta && /* @__PURE__ */ jsxs(ResourcePreviewGridItemMetaContent, {
        children: [
          !appearence?.hideTitle && /* @__PURE__ */ jsx(ResourcePreviewName, {
            showRoot: appearence?.showRootInFilename
          }),
          !appearence?.hideComment && (comment || resource.isDirectShared) && /* @__PURE__ */ jsxs(CommentAndShareContainer, {
            uncontainedPreview: !previewVariant,
            children: [
              comment && /* @__PURE__ */ jsx(ResourcePreviewPinnedComment, {
                comment
              }),
              resource.isDirectShared && /* @__PURE__ */ jsx(IconSharedWithPublic, {})
            ]
          }),
          !appearence?.hideTags && /* @__PURE__ */ jsx(ResourcePreviewTags, {
            compactAbsolute: previewVariant === "full-thumbnail-image",
            tags
          })
        ]
      })
    ]
  });
};
const ResourcePreviewGridItem = (props) => {
  const { children, appearence: appearenceProp, draggableRef, onMouseDown, className, isSelected, isSelectable, onClickResource, ...rest } = props;
  const uiConfigContext = useResourcePreviewUiConfigContext();
  const setQueryResourceDetailData = useSetQueryResourceDetailData();
  const appearence = {
    ...uiConfigContext?.appearence,
    ...appearenceProp
  };
  const innerProps = {
    ...rest,
    appearence
  };
  const { resource } = useResourceDataContextSafe();
  const previewVariant = getResourcePreviewVariant(resource);
  const ref = React__default.useRef(null);
  const onOpen = onClickResource ? () => onClickResource(resource) : void 0;
  const externalUrl = resource.originUrl || resource._meta?.externalData?.url;
  return /* @__PURE__ */ jsxs(PreviewContainerStyled, {
    ...rest,
    className,
    children: [
      externalUrl && /* @__PURE__ */ jsx(FloatingExternalLinkButton, {
        as: LinkExternal,
        href: getOriginUrlWithHash(externalUrl, resource.id),
        "data-preview-resource-external-link": true
      }),
      /* @__PURE__ */ jsx(PreviewGridSizingContainer, {
        variant: previewVariant,
        ref,
        minusBaseHeight: isResourceDetailFolder(resource) ? 0 : uiConfigContext?.minusBaseHeight,
        isSelected,
        children: /* @__PURE__ */ jsx(PreviewGridInnerLayout, {
          "data-resource-preview-type": "grid",
          onClick: onOpen,
          role: onOpen ? "link" : void 0,
          ref: draggableRef,
          onMouseDown,
          onMouseOver: () => {
            setQueryResourceDetailData(resource);
          },
          isSelected,
          "data-selectable-id": resource.isDraft || !isSelectable ? void 0 : resource?.id,
          children: /* @__PURE__ */ jsx(Inner, {
            ...innerProps
          })
        })
      })
    ]
  });
};
const PreviewContainerStyled = styled(PreviewGridContainer).withConfig({
  displayName: "ResourcePreviewGridItem__PreviewContainerStyled",
  componentId: "sc-fc6af7d4-0"
})([
  `&:not(:hover) `,
  `{opacity:0;scale:0.8;}`
], FloatingExternalLinkButton);
const ResourcePreviewGridItemContentPreview = styled.div.withConfig({
  displayName: "ResourcePreviewGridItem__ResourcePreviewGridItemContentPreview",
  componentId: "sc-fc6af7d4-1"
})([
  `flex-shrink:0;display:flex;align-items:center;justify-content:center;border-radius:1rem;position:relative;`,
  `{flex-shrink:1;flex-grow:1;border-radius:0;overflow:hidden;}`
], containerPreviewCompact);
const CommentAndShareContainer = styled(Flex).attrs({
  alignItems: "center",
  justifyContent: "center",
  gap: "elementsContainer"
}).withConfig({
  displayName: "ResourcePreviewGridItem__CommentAndShareContainer",
  componentId: "sc-fc6af7d4-2"
})([
  `height:`,
  `px;`,
  `{order:4;padding:0.6875rem;background:`,
  `;width:100%;height:38px;`,
  `}`
], COMMENT_SECTION_HEIGHT, containerPreviewCompact, cssVar["color-bg-primary"], (p) => p.uncontainedPreview && css([
  `height:28.5px;padding-top:0;`
]));

export { ResourcePreviewGridItem };
//# sourceMappingURL=ResourcePreviewGridItem.js.map
